import { useCallback } from 'react';
import css from './index.module.scss';
import { Button, IconNode, Loader, Modal, Typography } from '@components/base';
import { DragDropUpload, InputDatePicker, TextField } from '@components/common';
import { useForm, Controller, useWatch, SubmitHandler } from 'react-hook-form';
import Images from '@assets/images';
import notify from '@helpers/toastify-helper';
import { uploadProofOfDelivery } from '@services/task.service';
import { podSchema } from '@helpers/yup/add-proof-of-delivery.schema';
import { yupResolver } from '@hookform/resolvers/yup';

interface FormType {
  document: File | null;
  delivery_date: Date | null;
  remarks: string;
}

interface AddPaymentDetailsProps {
  open: boolean;
  onClose?: () => void;
  taskId: string;
  sendStatusToCard: (arg: boolean) => void;
  onComplete?: () => void;
  taskName?: string;
}

const AddProofOfDeliveryModal = (props: AddPaymentDetailsProps) => {
  const { open, onClose, sendStatusToCard, onComplete, taskName } = props;
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors }
  } = useForm<FormType>({
    resolver: yupResolver(podSchema),
    defaultValues: {
      document: null,
      // delivery_date: null,
      remarks: ''
    }
  });

  const watch = useWatch({ name: 'document', control });

  const handleFileSelect = (file: any[]) => {
    if (file.length) {
      setValue('document', file[0]);
    }
  };

  const handleDeleteFile = useCallback(() => {
    setValue('document', null);
  }, []);

  const handleFormSubmit: SubmitHandler<FormType> = async (data) => {
    if (!data.document) {
      notify({
        severity: 'error',
        message: 'Please upload a document'
      });
      return;
    }

    const formData = new FormData();
    formData.append('pod_document', data.document);
    formData.append('remarks', data.remarks);

    const response = await uploadProofOfDelivery(props.taskId, formData);
    if (response.success) {
      notify({
        severity: 'success',
        message: 'Proof of delivery uploaded successfully!'
      });
      onComplete?.();
      onClose?.();
      sendStatusToCard(true);
    } else {
      notify({
        severity: 'error',
        message: response.error ?? 'Something went wrong!'
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={css.mainWrapper}>
        <div className={css.headerWrapper}>
          <Typography variant="h4">Upload {taskName}</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </div>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)} className={css.formWrapper}>
          <div className={css.fieldsWrapper}>
            {/* <Controller
              name="delivery_date"
              control={control}
              render={({ field, fieldState }) => (
                <InputDatePicker
                  {...field}
                  required
                  label="Delivery Date"
                  onSelect={(day: Date | undefined) => field.onChange(day ?? null)}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  rootClassName={css.fieldSpacing}
                />
              )}
            /> */}

            <label htmlFor="Acceptance">
              <Typography variant="p" className={css.uploadWrapper}>
                Upload Proof
              </Typography>
              <Controller
                name="document"
                control={control}
                render={() => <DragDropUpload onDrop={handleFileSelect} required />}
              />
              {watch && (
                <div className={css.fileUploadState}>
                  <Typography variant="p" className={css.fileName}>
                    {watch.name}
                  </Typography>
                  <IconNode
                    src={Images.deleteRed}
                    alt="delete icon"
                    component="button"
                    className={css.deleteButton}
                    onClick={handleDeleteFile}
                  />
                </div>
              )}
            </label>

            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  inputMode="text"
                  label="Remarks"
                  placeholder="Add remarks if any"
                  rootClassName={css.inputFieldWrapper}
                />
              )}
            />
          </div>
          <div className={css.actionButtonWrapper}>
            <Button variant="outlined-secondary" onClick={onClose} className={css.actionButton}>
              Cancel
            </Button>
            <Button type="submit" className={css.actionButton}>
              Save
            </Button>
          </div>
        </form>
        <Loader open={isSubmitting} />
      </div>
    </Modal>
  );
};

export default AddProofOfDeliveryModal;
